<template>
  <div class="dash-filter-bar">
    <section class="dash-filter-bar__header">
      <section class="dash-filter-bar__title-area">
        <section
          class="dash-filter-bar__title-wrapper"
        >
          <header class="dash-filter-bar__title">
            {{ title || titlePlaceholder }}
          </header>

          <v-btn
            v-if="isEditor"
            v-tooltip="$t('components.DashFilterBar.label.tooltip')"
            fab
            small
            plain
            depressed
            class="dash-filter-bar__edit-title hide-tooltip"
            @click="openDialog"
          >
            <StIcon
              color="var(--ds-color-icon-2)"
              icon-name="pencil-outline"
            />
          </v-btn>
        </section>
      </section>

      <section class="dash-filter-bar__button-area">
        <v-menu
          v-if="showDashboardExportFeature && !isLoadingExportComponents"
          offset-y
          class="mass-export-select"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-tooltip="exportTooltip"
              depressed
              plain
              x-small
              rounded
              :disabled="!existsCardToExport"
              class="mass-export-select__button mr-4"
              v-bind="attrs"
              dark
              v-on="on"
              @click.stop="openExportOptions()"
            >
              <DsIcon
                name="download"
                depressed
                size="small"
                :class="`mass-export-select__button-icon${isExportIconDisabled}`"
                dark
              />
            </v-btn>
          </template>
          <v-list
            nav
            class="mass-export-select__options mt-1"
          >
            <v-list-item class="mass-export-select__item pa-0">
              <v-btn
                x-large
                text
                block
                class="mass-export-select__item-content px-4 py-2"
                @click.stop="exportAllComponents()"
              >
                {{ exportTypeValue }}
              </v-btn>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-progress-circular
          v-if="showDashboardExportFeature && isLoadingExportComponents"
          :size="24"
          :width="2"
          class="mass-export-select__progress-spinner my-1 mr-4"
          indeterminate
        />
        <v-btn
          class="text-capitalize"
          @click.stop="miscDrawer = !miscDrawer"
        >
          <StIcon
            icon-name="chart-bar"
            class="mr-3"
          />
          {{ chooseCardsTitle }}
        </v-btn>

        <AutoReload v-if="!isMobile" />

        <AddCardsButton
          class="ml-3"
          @open-library="toggleDashAddModal()"
        >
          <template
            v-if="showOpenLibraryButton"
            #icon
          >
            <img :src="addIconSrc">
          </template>
          <DsIcon
            v-else
            name="add"
            size="small"
            dark
          />
        </AddCardsButton>
      </section>

      <section
        v-if="description"
        class="dash-filter-bar__description-area"
      >
        <pre class="dash-filter-bar__description">{{ description }}</pre>
      </section>
    </section>

    <div
      class="dash-filter-bar__title-edit-modal"
    >
      <AddDescriptionModal
        v-if="editTitleModal"
        :title="title"
        :description="description"
        @cancel="cancelAction"
        @confirm="confirmAction"
      />
    </div>
  </div>
</template>

<script>
import cardsModel from '@/mixins/cards-model';
import exportModel from '@/mixins/export-model';
import StIcon from '@stilingue/st-icon';
import { DsIcon } from '@ds/icons';
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex';
import AddDescriptionModal from '@/components/AddDescriptionModal';
import { MUTATE } from '@/store/modules/mutation_types';
import AutoReload from '@/components/AutoReload';
import { EXTENSION_TYPES } from '@/utils/constants';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import vectorDark from '@/assets/img/vector-dark.svg';
import vectorLight from '@/assets/img/vector-light.svg';
import { Flags } from '@/plugins/rollout';
import AddCardsButton from '@/components/AddCardsButton.vue';

export default {
  name: 'DashFilterBar',
  components: {
    StIcon,
    DsIcon,
    AutoReload,
    AddDescriptionModal,
    AddCardsButton,
  },
  mixins: [
    cardsModel,
    exportModel,
  ],
  data() {
    return {
      exportTypeValue: EXTENSION_TYPES.PNG.name,
      exportTooltip: this.$t('components.DashFilterBar.label.exportTooltip'),
      titlePlaceholder: this.$t('components.DashFilterBar.label.titlePlaceholder'),
      title: '',
      description: '',
      originalTitle: '',
      originalDescription: '',
      inputLoading: false,
      editTitleModal: false,
      chooseCardsTitle: this.$t('components.DashFilterBar.label.chooseCardsTitle'),
      addCardTooltip: this.$t('components.DashFilterBar.label.addCardTooltip'),
      addIconDark: vectorDark,
      addIconLight: vectorLight,
    };
  },
  computed: {
    ...mapState('misc', [
      'drawer',
      'addCardDrawer',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    miscDrawer: {
      get() {
        return this.drawer;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'drawer',
          with: newValue,
        });
      },
    },
    addIconSrc() {
      return this.$vuetify.theme.dark ? this.addIconDark : this.addIconLight;
    },
    miscAddCardDrawer: {
      get() {
        return this.addCardDrawer;
      },
      set(newValue) {
        this.mutateMisc({
          property: 'addCardDrawer',
          with: newValue,
        });
      },
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isExportIconDisabled() {
      return !this.existsCardToExport ? '--disabled' : '';
    },
    showOpenLibraryButton() {
      return this.isEnabled(Flags.showAddCardShortcut);
    },
    showDashboardExportFeature() {
      return this.isEnabled(Flags.showDashboardExport);
    },
  },
  mounted() {
    this.title = this.activeDashboard.data.title || '';
    this.originalTitle = this.activeDashboard.data.title || '';

    this.description = this.activeDashboard.data.description || '';
    this.originalDescription = this.activeDashboard.data.description || '';
  },
  methods: {
    ...mapActions('dashboard', [
      'saveTitle',
      'saveDescription',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    toggleDashAddModal() {
      this.miscAddCardDrawer = !this.miscAddCardDrawer;
    },
    openDialog() {
      this.editTitleModal = !this.editTitleModal;

      trackEventWithUserProperties('ST Dashboard TitleEdit Click', { Title: this.title });
    },
    cancelAction() {
      this.title = this.originalTitle;
      this.description = this.originalDescription;

      this.editTitleModal = !this.editTitleModal;
    },
    confirmAction({ title, description }) {
      this.title = title;
      this.description = description;

      this.inputLoading = true;
      this.saveTitle(this.title).then(() => {
        this.originalTitle = this.title;
      });

      this.saveDescription(this.description).then(() => {
        this.originalDescription = this.description;
        this.inputLoading = false;
      });

      trackEventWithUserProperties('ST Dashboard Title Edit', {
        Title: this.title,
        Description: this.description,
      });

      this.editTitleModal = !this.editTitleModal;
    },
    openExportOptions() {
      trackEventWithUserProperties('ST Dashboard ComponentsExport Click', {});
    },
  },
};
</script>

<style scoped lang="scss">
  .mass-export-select {
    &__button {
      padding: 6px !important;
      height: auto !important;
    }

    &__button-icon {
      background-color: var(--ds-color-icon-1);
    }

    &__button-icon--disabled {
      background-color: var(--ds-color-gray-2);
    }

    &__options {
      width: 112px;
      border: 1px solid var(--ds-color-gray-3);
      box-shadow: var(-ds-light-dropshadow-1);
      border-radius: var(--ds-border-radius-sm);
      padding: var(--ds-spacing-stack-quark) !important;
    }

    &__item {
      display: flex;
    }

    &__item-content {
      justify-content: flex-start;
    }

    &__progress-spinner {
      color: var(--ds-color-icon-highlight);
    }
  }

  .hide-tooltip {
    display: none;
  }

  .dash-filter-bar {
    display: flex;
    flex-direction: column;

    &__header {
      display: grid;
      grid-template-areas:
        "title button"
        "description description";
      grid-template-columns: 2fr 1fr;
      margin-bottom: var(--ds-spacing-stack-xs);
    }

    &__title-wrapper {
      display: flex;

      &:hover > .hide-tooltip {
        display: block;
      }
    }

    &__title-area {
      grid-area: title;
      display: flex;
    }

    &__button-area {
      grid-area: button;
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }

    &__title {
      display: initial;
      height: 64px !important;
      max-height: 48px !important;
      padding-right: var(--ds-spacing-inset-nano);
      font-size: var(--ds-font-size-xl) !important;
      font-weight: var(--ds-font-weight-semibold);
      line-height: 48px;
      color: var(--ds-color-reverse);
    }

    &__edit-title {
      align-self: center;
    }

    &__description-area {
      grid-area: description;
      margin: var(--ds-spacing-stack-nano) 0 0 0;
    }

    &__description {
      margin: 0;
      font: var(--ds-font-family-base);
      color: var(--ds-color-text-2);
      text-align: left;
      white-space: pre;
    }

    &__title-edit-modal {
      z-index: 10;
    }
  }

  ::v-deep {
    .v-btn::before {
      background-color: var(--ds-color-icon-2);
    }

    .v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
      background-color: transparent !important;
    }
  }
</style>
